import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { RefreshCw, ExternalLink, Clock, MapPin, AlertTriangle, Download, Calendar, Filter, ChevronDown } from 'lucide-react';

export default function NewsFeed() {
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('All');
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [countdown, setCountdown] = useState(30);
  const [lastUpdate, setLastUpdate] = useState(null);
  
  // Date filtering
  const [dateRange, setDateRange] = useState('all');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showDatePicker, setShowDatePicker] = useState(false);

  // Pre-set date ranges
  const dateRanges = [
    { id: 'today', label: 'Today', days: 0 },
    { id: 'week', label: 'Last 7 Days', days: 7 },
    { id: 'month', label: 'Last 30 Days', days: 30 },
    { id: '3months', label: 'Last 3 Months', days: 90 },
    { id: '6months', label: 'Last 6 Months', days: 180 },
    { id: 'year', label: 'Last Year', days: 365 },
    { id: 'all', label: 'All Time', days: null },
    { id: 'custom', label: 'Custom Range', days: 'custom' }
  ];

  useEffect(() => {
    loadNews();
  }, [dateRange, startDate, endDate]);

  useEffect(() => {
    if (!autoRefresh) return;
    const refreshInterval = setInterval(loadNews, 30000);
    const countdownInterval = setInterval(() => {
      setCountdown(prev => prev > 0 ? prev - 1 : 30);
    }, 1000);
    return () => {
      clearInterval(refreshInterval);
      clearInterval(countdownInterval);
    };
  }, [autoRefresh]);

  const loadNews = async () => {
    setLoading(true);
    try {
      // Build query params for date filtering
      let params = {};
      
      if (dateRange === 'custom' && startDate && endDate) {
        params.start_date = startDate;
        params.end_date = endDate;
      } else if (dateRange !== 'all' && dateRange !== 'custom') {
        const range = dateRanges.find(r => r.id === dateRange);
        if (range && range.days !== null) {
          const end = new Date();
          const start = new Date();
          start.setDate(start.getDate() - range.days);
          params.start_date = start.toISOString().split('T')[0];
          params.end_date = end.toISOString().split('T')[0];
        }
      }
      
      const queryString = new URLSearchParams(params).toString();
      const url = queryString ? `${API}/news/feed?${queryString}` : `${API}/news/feed`;
      
      const response = await axios.get(url);
      setArticles(response.data);
      setLastUpdate(new Date());
      setCountdown(30);
    } catch (error) {
      console.error('Error loading news:', error);
    } finally {
      setLoading(false);
    }
  };

  const downloadNews = () => {
    const dataStr = JSON.stringify(filteredArticles, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = window.URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `DefenseWatch_News_${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDateRangeChange = (rangeId) => {
    setDateRange(rangeId);
    if (rangeId !== 'custom') {
      setShowDatePicker(false);
    } else {
      setShowDatePicker(true);
      // Set default custom range to last 30 days
      const end = new Date();
      const start = new Date();
      start.setDate(start.getDate() - 30);
      setStartDate(start.toISOString().split('T')[0]);
      setEndDate(end.toISOString().split('T')[0]);
    }
  };

  // Apply both threat level and date filtering
  const filteredArticles = articles.filter(article => {
    // Threat level filter
    if (filter !== 'All' && article.threat_level !== filter) {
      return false;
    }
    return true;
  });

  const getThreatColor = (level) => {
    switch (level) {
      case 'Critical': return 'text-[#FF3B30] border-[#FF3B30]/20 bg-[#FF3B30]/5';
      case 'High': return 'text-[#FFD60A] border-[#FFD60A]/20 bg-[#FFD60A]/5';
      case 'Medium': return 'text-[#00F0FF] border-[#00F0FF]/20 bg-[#00F0FF]/5';
      default: return 'text-[#00FF9D] border-[#00FF9D]/20 bg-[#00FF9D]/5';
    }
  };

  const getDateRangeLabel = () => {
    const range = dateRanges.find(r => r.id === dateRange);
    if (dateRange === 'custom' && startDate && endDate) {
      return `${startDate} to ${endDate}`;
    }
    return range?.label || 'All Time';
  };

  return (
    <div className="h-full overflow-hidden flex flex-col" data-testid="news-feed">
      {/* Header */}
      <div className="glass-panel border-b border-white/10 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-3xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>MILITARY NEWS FEED</h1>
            <p className="text-xs text-zinc-400 font-mono mt-1">
              Auto-refresh: {autoRefresh ? `${countdown}s` : 'OFF'}
              {lastUpdate && ` • Updated: ${lastUpdate.toLocaleTimeString()}`}
              {` • ${filteredArticles.length} articles`}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`flex items-center gap-2 px-3 py-2 text-xs font-bold uppercase ${
                autoRefresh ? 'btn-primary' : 'btn-secondary'
              }`}
            >
              <Clock className="w-4 h-4" />
              <span>{autoRefresh ? '30s' : 'OFF'}</span>
            </button>
            <button
              onClick={downloadNews}
              className="flex items-center gap-2 px-4 py-2 btn-secondary"
              title="Download news for offline use"
              data-testid="download-news-btn"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              data-testid="refresh-news-button"
              onClick={loadNews}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 btn-secondary disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>REFRESH</span>
            </button>
          </div>
        </div>

        {/* Filters Row */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Threat Level Filter */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500 uppercase font-mono">Threat:</span>
            <div className="flex gap-1">
              {['All', 'Critical', 'High', 'Medium', 'Low'].map((level) => (
                <button
                  key={level}
                  data-testid={`filter-${level.toLowerCase()}`}
                  onClick={() => setFilter(level)}
                  className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-all ${
                    filter === level
                      ? 'bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50'
                      : 'bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>

          {/* Date Range Filter */}
          <div className="flex items-center gap-2">
            <span className="text-[10px] text-zinc-500 uppercase font-mono">Date:</span>
            <div className="relative">
              <button
                onClick={() => setShowDatePicker(!showDatePicker)}
                className="flex items-center gap-2 px-3 py-1.5 text-[10px] font-bold uppercase bg-white/5 text-zinc-300 border border-white/10 hover:bg-white/10 transition-all"
                data-testid="date-filter-btn"
              >
                <Calendar className="w-3 h-3" />
                <span>{getDateRangeLabel()}</span>
                <ChevronDown className="w-3 h-3" />
              </button>

              {/* Date Range Dropdown */}
              {showDatePicker && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-[#0a0f14] border border-white/10 shadow-xl z-50">
                  {/* Preset Ranges */}
                  <div className="p-2 border-b border-white/10">
                    <div className="text-[10px] text-zinc-500 uppercase font-mono mb-2 px-2">Quick Select</div>
                    <div className="grid grid-cols-2 gap-1">
                      {dateRanges.filter(r => r.id !== 'custom').map((range) => (
                        <button
                          key={range.id}
                          onClick={() => handleDateRangeChange(range.id)}
                          className={`px-2 py-1.5 text-xs text-left transition-all ${
                            dateRange === range.id
                              ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                              : 'text-zinc-400 hover:bg-white/5'
                          }`}
                        >
                          {range.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Range */}
                  <div className="p-2">
                    <button
                      onClick={() => handleDateRangeChange('custom')}
                      className={`w-full px-2 py-1.5 text-xs text-left mb-2 transition-all ${
                        dateRange === 'custom'
                          ? 'bg-[#00FF9D]/10 text-[#00FF9D]'
                          : 'text-zinc-400 hover:bg-white/5'
                      }`}
                    >
                      Custom Range
                    </button>
                    
                    {dateRange === 'custom' && (
                      <div className="space-y-2">
                        <div>
                          <label className="text-[10px] text-zinc-500 block mb-1">Start Date</label>
                          <input
                            type="date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            max={endDate || new Date().toISOString().split('T')[0]}
                            className="w-full bg-black/50 border border-white/10 text-white px-2 py-1 text-xs"
                            data-testid="start-date-input"
                          />
                        </div>
                        <div>
                          <label className="text-[10px] text-zinc-500 block mb-1">End Date</label>
                          <input
                            type="date"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            min={startDate}
                            max={new Date().toISOString().split('T')[0]}
                            className="w-full bg-black/50 border border-white/10 text-white px-2 py-1 text-xs"
                            data-testid="end-date-input"
                          />
                        </div>
                        <button
                          onClick={() => { loadNews(); setShowDatePicker(false); }}
                          className="w-full py-1.5 text-xs font-bold bg-[#00FF9D]/10 text-[#00FF9D] border border-[#00FF9D]/50 hover:bg-[#00FF9D]/20"
                        >
                          APPLY
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        {loading && articles.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <RefreshCw className="w-8 h-8 text-[#00FF9D] animate-spin mx-auto mb-4" />
              <p className="text-zinc-400 font-mono text-sm">Loading intelligence feed...</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
            {filteredArticles.map((article) => (
              <div
                key={article.id}
                data-testid="news-article-card"
                className="glass-panel tactical-border p-4 hover:border-[#00FF9D]/30 transition-all cursor-pointer group"
                onClick={() => navigate(`/article/${article.id}`, { state: { article } })}
              >
                {/* Source & Threat Level */}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-[10px] text-zinc-400 uppercase tracking-wider font-mono">{article.source}</span>
                  <span className={`px-2 py-1 text-[10px] font-bold uppercase tracking-wider border ${getThreatColor(article.threat_level)}`}>
                    {article.threat_level}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-sm font-bold text-white mb-2 group-hover:text-[#00FF9D] transition-colors line-clamp-2">
                  {article.title}
                </h3>

                {/* Summary */}
                <p className="text-xs text-zinc-400 mb-3 line-clamp-3">{article.summary}</p>

                {/* Meta */}
                <div className="flex items-center gap-4 text-[10px] text-zinc-500 font-mono">
                  <div className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{new Date(article.published).toLocaleDateString()}</span>
                  </div>
                  {article.region && (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      <span>{article.region}</span>
                    </div>
                  )}
                </div>

                {/* Action */}
                <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                  <span className="text-[10px] text-[#00FF9D] uppercase tracking-wider font-bold">View Analysis</span>
                  <ExternalLink className="w-4 h-4 text-zinc-500 group-hover:text-[#00FF9D] transition-colors" />
                </div>
              </div>
            ))}
          </div>
        )}

        {filteredArticles.length === 0 && !loading && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <AlertTriangle className="w-8 h-8 text-zinc-600 mx-auto mb-4" />
              <p className="text-zinc-400 font-mono text-sm">No articles found for the selected date range</p>
              <button
                onClick={() => handleDateRangeChange('all')}
                className="mt-4 px-4 py-2 text-xs bg-white/5 text-zinc-400 border border-white/10 hover:bg-white/10"
              >
                Show All Articles
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
